import { AxiosResponse } from "axios";
import { IValidationResult } from "lobbie";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import CenteredLoadingSpinner from "src/components/shared/loading/CenteredLoadingSpinner";
import {
    BASE_PUBLIC_API_ROUTE,
    DEFAULT_ERROR_MESSAGE,
    IS_MOBILE_PHONE,
    LOBBIE_STORAGE,
} from "src/constants";
import { useLobbieAccountPublicUuid } from "src/hooks/accounts/accounts";
import { useAxiosPublicPost } from "src/hooks/useAxios";
import { useLogoutNoRedirect } from "src/hooks/user";
import { handleError, isFailedRequest, localGet, logDev, notify, sessionGet } from "src/utils";

const PatientFormsCompletionConfirmation: React.FC = () => {
    const poster = useAxiosPublicPost();
    const logout = useLogoutNoRedirect(true);
    // useLogoutOnPageClose(true);

    const [signedURL, setSignedURL] = useState<string | undefined>();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isLoggedOut, setLoggedOut] = useState<boolean>(false);

    const accountPublicUuid = useLobbieAccountPublicUuid();

    const locationId = useMemo(
        () => localGet(LOBBIE_STORAGE.Session.Patients.Forms.Anonymous.LocationId),
        [],
    );
    const formTemplatePublicUuid = useMemo(
        () => localGet(LOBBIE_STORAGE.Session.Patients.Forms.Anonymous.FormTemplatePublicUuid),
        [],
    );
    const formTemplateGroupPublicUuid = useMemo(
        () => localGet(LOBBIE_STORAGE.Session.Patients.Forms.Anonymous.FormTemplateGroupPublicUuid),
        [],
    );

    const s3ObjectPath = useMemo(
        () =>
            sessionGet(LOBBIE_STORAGE.Session.Patients.Forms.S3ObjectPath) ||
            sessionGet(LOBBIE_STORAGE.Session.Patients.Forms.CacheKey),
        [],
    );

    const isLogout = useMemo(() => !s3ObjectPath || signedURL, [s3ObjectPath, signedURL]);
    const handleLogout = useCallback(
        () =>
            isLoggedOut
                ? Promise.resolve(undefined)
                : logout()
                      .then(() => setLoggedOut(true))
                      .catch(handleError),
        [isLoggedOut, logout],
    );

    useEffect(() => {
        if (isLogout) {
            handleLogout().catch(console.error);
        }
    }, [handleLogout, isLogout]);

    useEffect(() => {
        if (!s3ObjectPath || signedURL) {
            return;
        }
        setLoading(true);

        const getter = setInterval(() => {
            const route = `/${BASE_PUBLIC_API_ROUTE}/patient/pdf`;
            const data = {
                s3ObjectPath,
            };
            logDev("PatientFormsCompletionConfirmation.handleGetFile -", data);
            handleLogout().catch(console.error);
            poster(route, data, logDev)
                .then((response: AxiosResponse | void) => {
                    const result =
                        response && (response.data as IValidationResult | { signedURL: string });
                    if (!result || isFailedRequest(result)) {
                        setLoading(false);
                        handleLogout().catch(console.error);
                    } else if (
                        "message" in result &&
                        result.success &&
                        result.message === "preparing"
                    ) {
                        logDev("Still preparing file download...");
                    } else {
                        handleLogout().catch(console.error);
                        setSignedURL((result as { signedURL: string }).signedURL);
                        clearInterval(getter);
                        setLoading(false);
                    }
                })
                .catch((error) => {
                    setLoading(false);
                    handleLogout().catch(console.error);
                    handleError(error);
                });
        }, 3000);

        return () => getter && clearTimeout(getter);
    }, [s3ObjectPath, signedURL, poster, handleLogout]);

    const openForms = useCallback(() => {
        if (signedURL) {
            const opened = window.open(signedURL, "_blank");
            opened?.focus();
        } else {
            notify({
                level: "warning",
                title: "Cound not find forms.",
                message: DEFAULT_ERROR_MESSAGE,
            });
        }
    }, [signedURL]);

    const renderPDF = useMemo(() => {
        //Disbaility Help
        //Convert to a property if other clients as for it
        if (accountPublicUuid === "4404f450-cfa5-48dc-b66b-54a0b67359d0") {
            return null;
        }
        if (signedURL) {
            return (
                <>
                    <hr className="mw-50 text-center mx-auto mt-4" />
                    <div className="my-4">
                        <Button variant="outline-primary" onClick={openForms} disabled={isLoading}>
                            <FiDownload size="1.2em" />
                            &nbsp;Download a copy of your forms
                        </Button>
                    </div>
                    <div className="fs-7 gray2 mt-1">This download will be valid for 24 hours.</div>
                </>
            );
        } else if (isLoading) {
            return (
                <>
                    <hr className="mw-50 text-center mx-auto mt-4" />
                    <CenteredLoadingSpinner message="Preparing PDF files of your forms, please wait..." />
                </>
            );
        } else {
            return null;
        }
    }, [signedURL, isLoading, openForms, accountPublicUuid]);

    const renderRestart = useMemo(() => {
        if (
            accountPublicUuid &&
            locationId &&
            (formTemplatePublicUuid || formTemplateGroupPublicUuid)
        ) {
            const link = formTemplatePublicUuid
                ? `https://${window.location.host}/static/form/${accountPublicUuid}/${locationId}/${formTemplatePublicUuid}`
                : `https://${window.location.host}/static/forms/${accountPublicUuid}/${locationId}/${formTemplateGroupPublicUuid}`;

            return (
                <>
                    &nbsp;
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            window.location.href = link;
                        }}
                    >
                        {IS_MOBILE_PHONE ? "Tap" : "Click"} here to begin the form(s) again
                    </Button>
                </>
            );
        } else {
            return <span>You may now close this tab.</span>;
        }
    }, [accountPublicUuid, formTemplateGroupPublicUuid, formTemplatePublicUuid, locationId]);

    return (
        <div className="col w-100 mx-auto text-center mt-5">
            <img alt="success" width={"150"} src="/check.png"></img>
            <div className="mt-4">
                <h3>All forms complete!</h3>
            </div>
            <div className="fs-6 gray2">All forms have been completed successfully.</div>
            <div className="fs-6 gray2 mt-2">{renderRestart}</div>
            {renderPDF}
            <hr className="mw-50 text-center mx-auto" />
            <div className="row mt-4 align-items-center">
                <div className="col">
                    <div>
                        <a
                            className="blue underline"
                            href="https://lobbie.com/gl/formslp?utm_source=growthloop&utm_medium=growthloop&utm_campaign=formsty"
                            target="_blank"
                        >
                            Try Lobbie Forms at your organization&nbsp;
                            <FiExternalLink />
                        </a>
                        <div>
                            <img
                                className="login-logo mt-1"
                                src="/powered_by_lobbie.svg"
                                alt="Powered by Lobbie"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default PatientFormsCompletionConfirmation;
